/** @jsx jsx */
import React from 'react';
import { css, jsx } from '@compiled/react';
import { CardAction, ElementName, SmartLinkSize, TitleBlock } from '@atlaskit/smart-card';
import { CardSSR as Card } from '@atlaskit/smart-card/ssr';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { RemoveButton } from './remove-button/index.tsx';

const uiOptions = {
	clickableContainer: true,
	hideBackground: true,
	hideElevation: true,
	hidePadding: true,
	size: SmartLinkSize.Medium,
};

const actionOptions = {
	hide: false,
	exclude: [CardAction.ChangeStatusAction],
};

type TitleBlockProps = Parameters<typeof TitleBlock>[0];
type Props = {
	/** URL to resolve for the smart link. */
	url: string;
	/** When defined this will display a remove button after the link that calls `onRemove` when clicked. */
	onRemove?: () => void;
	/** Flag to display hover preview on hover. Defaults to `true`. */
	showHoverPreview?: boolean;
	/**
	 * Ref passed into the link <a> element
	 */
	anchorRef?: React.Ref<HTMLAnchorElement>;
	onError?: () => void;
} & Pick<TitleBlockProps, 'anchorTarget' | 'text'>;

export const SimpleInlineSmartLink = ({
	onRemove,
	url,
	anchorTarget,
	showHoverPreview = true,
	text,
	anchorRef,
	onError,
}: Props) => {
	return (
		<Box paddingBlock="space.025" paddingInline="space.050" xcss={inlineStyles}>
			<Card
				appearance="inline"
				url={url}
				ui={uiOptions}
				actionOptions={actionOptions}
				showHoverPreview={showHoverPreview}
				onError={onError}
			>
				<TitleBlock
					hideTitleTooltip
					maxLines={1}
					metadata={[
						{
							name: ElementName.State,
						},
					]}
					anchorTarget={anchorTarget ?? '_self'}
					text={text}
					css={titleBlockStyles}
					anchorRef={anchorRef}
				/>
			</Card>
			{onRemove && <RemoveButton onClick={onRemove} />}
		</Box>
	);
};

const inlineStyles = xcss({
	backgroundColor: 'elevation.surface.raised',
	borderColor: 'color.border',
	borderStyle: 'solid',
	borderWidth: 'border.width',
	borderRadius: 'border.radius',
	display: 'inline-flex',
	gap: 'space.050',
});

const titleBlockStyles = css({
	gap: token('space.050'),
});
